import type {
  MeasurementStatus,
  NormalizedGeometry,
  Severity,
  ValidationReport,
  VisualMeasurement,
} from '@photovalidator/core';

/**
 * Overlay plan: measurements → SVG shapes.
 *
 * This module is pure (no DOM), which is what makes it testable and makes the
 * central promise verifiable: **the overlay draws exactly the geometry the
 * validator produced**. It never computes head size, eye line or pose itself,
 * and it holds no thresholds of its own.
 *
 * Coordinates stay normalized (0…1) — the SVG uses `viewBox="0 0 1 1"` and lives
 * in the same element as the photo, so both are described by one unchanged
 * rectangle: the validator never transforms the picture.
 */

export type OverlayElementName = 'line' | 'rect' | 'ellipse' | 'circle' | 'polygon';

export interface OverlayShape {
  key: string;
  measurementId: string;
  status: MeasurementStatus;
  severity: Severity;
  role: 'guide' | 'measurement' | 'problem' | 'frame';
  element: OverlayElementName;
  attributes: Record<string, number>;
  /** Set instead of `attributes` for polygons. */
  polygonPoints?: string;
  className: string;
  /** Screen-space-ish label attached to this shape, in normalized coordinates. */
  label?: OverlayLabel;
  /** Accessibility/tooltip text: the same message the card shows. */
  title: string;
}

export interface OverlayLabel {
  id: string;
  text: string;
  point: { x: number; y: number };
  status: MeasurementStatus;
  severity: Severity;
  tooltip: string;
  interactive: boolean;
  /**
   * Presentation role:
   *   value  — a measured number attached to a line;
   *   zone   — «SAFE HEAD ZONE», the allowed band itself;
   *   top / chin / eyes — boundary captions (detailed mode only);
   *   center — the frame centre marker.
   */
  role?: 'value' | 'zone' | 'top' | 'chin' | 'eyes' | 'center';
}

export interface ProblemMarker {
  id: string;
  checkId: string;
  rect: { x: number; y: number; width: number; height: number };
  message: string;
  severity: 'warning' | 'error';
}

export interface OverlayPlan {
  shapes: OverlayShape[];
  labels: OverlayLabel[];
  problemMarkers: ProblemMarker[];
  /** Landmark dots, only in detailed mode. */
  landmarks: Array<{ x: number; y: number }>;
  /** How many measurements were drawn/hidden, for the debug panel. */
  drawn: number;
  hidden: number;
}

export interface OverlayPlanOptions {
  /** Size of the content element in CSS pixels; only used to keep dots round. */
  baseSize: { width: number; height: number };
  showGuides: boolean;
  /** `live` draws the guides only (camera preview). */
  mode?: 'report' | 'live';
  detailMode: boolean;
  highlightId?: string;
}

const r = (value: number): number => Math.round(value * 1e5) / 1e5;

const attributeNameFor = (geometry: NormalizedGeometry): OverlayElementName => {
  switch (geometry.type) {
    case 'line':
      return 'line';
    case 'rect':
      return 'rect';
    case 'ellipse':
      return 'ellipse';
    case 'point':
      return 'circle';
    case 'polygon':
      return 'polygon';
    default:
      return 'line';
  }
};

/**
 * Converts one normalized shape into SVG attributes.
 *
 * `baseSize` lets a "point" become a round screen dot instead of an ellipse:
 * with `preserveAspectRatio="none"` a normalized radius would stretch with the
 * image aspect ratio.
 */
export const geometryToAttributes = (
  geometry: NormalizedGeometry,
  baseSize: { width: number; height: number },
  dotRadiusPx = 3.5,
): Record<string, number> | null => {
  switch (geometry.type) {
    case 'line': {
      const [x1, y1, x2, y2] = geometry.coordinates;
      return { x1: r(x1), y1: r(y1), x2: r(x2), y2: r(y2) };
    }
    case 'rect': {
      const [x, y, width, height] = geometry.coordinates;
      if (width <= 0 || height <= 0) return null;
      return { x: r(x), y: r(y), width: r(width), height: r(height) };
    }
    case 'ellipse': {
      const [cx, cy, rx, ry] = geometry.coordinates;
      if (rx <= 0 || ry <= 0) return null;
      return { cx: r(cx), cy: r(cy), rx: r(rx), ry: r(ry) };
    }
    case 'point': {
      const [cx, cy] = geometry.coordinates;
      return {
        cx: r(cx),
        cy: r(cy),
        r: r(
          Math.max(
            1 / Math.max(baseSize.width, 1),
            1 / Math.max(baseSize.height, 1),
          ) * dotRadiusPx,
        ),
      };
    }
    case 'polygon':
      // Point lists are rendered as `points="x,y …"`, handled by the caller.
      return geometry.coordinates.length >= 6 ? {} : null;
    default:
      return null;
  }
};

const polygonPoints = (geometry: NormalizedGeometry): string =>
  geometry.coordinates
    .reduce<string[]>((pairs, coordinate, index, all) => {
      if (index % 2 === 0) {
        const y = all[index + 1] ?? 0;
        pairs.push(`${r(coordinate)},${r(y)}`);
      }
      return pairs;
    }, [])
    .join(' ');

/** Should this measurement be visible on the photo right now? */
export const shouldDraw = (
  measurement: VisualMeasurement,
  options: OverlayPlanOptions,
): boolean => {
  const isGuide = measurement.guide === true;
  if (isGuide && !options.showGuides) return false;
  if (options.mode === 'live') return isGuide;
  if (measurement.geometry.length === 0) return false;
  if (options.detailMode) return true;
  if (measurement.id === options.highlightId) return true;
  /**
   * Normal mode shows the **guides only** — centre axis, eye line, head zone,
   * head top, chin and the face box. Findings are text in the checklist, not a
   * dozen coloured markers on the photo: the validator explains, it does not
   * paint over the picture.
   */
  return isGuide;
};

/** Vertical bounds of the allowed head band, in image coordinates. */
const headZoneLines = (measurement: VisualMeasurement): { top: number; bottom: number } | null => {
  const lines = measurement.geometry.filter((shape) => shape.type === 'line');
  if (lines.length < 2) {
    const rect = measurement.geometry.find((shape) => shape.type === 'rect');
    if (!rect || rect.type !== 'rect') return null;
    const [, y, , height] = rect.coordinates;
    return { top: y, bottom: y + height };
  }
  const ys = lines
    .map((line) => (line.type === 'line' ? line.coordinates[1] : Number.NaN))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  const top = ys[0];
  const bottom = ys[ys.length - 1];
  if (top === undefined || bottom === undefined) return null;
  return { top, bottom };
};

const labelFor = (
  measurement: VisualMeasurement,
  report: ValidationReport,
  options: OverlayPlanOptions,
): OverlayLabel | undefined => {
  const text = measurement.displayValue;
  const eyeLine = report.metrics.geometry?.eyeLineFromTop;
  const headBox = report.metrics.geometry?.headBox;

  const base = {
    id: `label:${measurement.id}`,
    status: measurement.status,
    severity: measurement.severity,
    // The tooltip states what was measured; the validator does not give advice.
    tooltip: measurement.message,
    interactive: measurement.interactive !== false,
  };

  switch (measurement.id) {
    case 'guide.eye_band':
    case 'eye_line': {
      if (eyeLine === undefined) return undefined;
      return {
        ...base,
        role: 'eyes',
        text: options.detailMode
          ? `EYES · ${text ?? '—'}`
          : text
            ? `Глаза ${text}`
            : 'Глаза',
        point: { x: 0.06, y: eyeLine },
      };
    }
    case 'guide.head_zone': {
      const lines = headZoneLines(measurement);
      if (!lines) return undefined;
      return {
        ...base,
        role: 'zone',
        text: text ? `SAFE HEAD ZONE · ${text}` : 'SAFE HEAD ZONE',
        // Inside the band, on the vertical centre line: never covers the face.
        point: { x: 0.5, y: Math.min(0.97, lines.top + 0.035) },
      };
    }
    case 'guide.head_measure':
    case 'head_size': {
      if (!headBox) return undefined;
      return {
        ...base,
        role: 'value',
        text: text ? `Голова ${text}` : 'Голова',
        point: { x: Math.min(0.98, headBox.x + headBox.width), y: headBox.y + headBox.height / 2 },
      };
    }
    case 'guide.center_line': {
      return {
        ...base,
        role: 'center',
        text: options.detailMode ? 'CENTER' : 'Центр',
        point: { x: 0.5, y: 0.02 },
      };
    }
    case 'face_position': {
      if (!headBox) return undefined;
      return {
        ...base,
        role: 'value',
        text: text ? `Центр ${text}` : 'Центр',
        point: { x: report.metrics.geometry?.faceCenterX ?? 0.5, y: headBox.y },
      };
    }
    default:
      return undefined;
  }
};

/**
 * Boundary captions (TOP / CHIN) for the head zone.
 *
 * Shown only in the detailed overlay: in the calm default view the user sees a
 * single «SAFE HEAD ZONE» band, which is enough to understand the requirement.
 */
const boundaryLabelsFor = (
  measurement: VisualMeasurement,
  options: OverlayPlanOptions,
): OverlayLabel[] => {
  if (measurement.id !== 'guide.head_zone' || !options.detailMode) return [];
  const lines = headZoneLines(measurement);
  if (!lines) return [];
  const base = {
    status: measurement.status,
    severity: measurement.severity,
    interactive: false,
  };
  return [
    {
      ...base,
      id: 'label:head_zone_top',
      role: 'top',
      text: 'TOP',
      point: { x: 0.07, y: lines.top },
      tooltip: 'Допустимое положение верхней границы головы (макушки).',
    },
    {
      ...base,
      id: 'label:head_zone_chin',
      role: 'chin',
      text: 'CHIN',
      point: { x: 0.07, y: lines.bottom },
      tooltip: 'Допустимое положение нижней границы головы (подбородка).',
    },
  ];
};

const shapeClassName = (
  role: OverlayShape['role'],
  measurement: VisualMeasurement,
  highlighted: boolean,
  geometry: NormalizedGeometry,
): string => {
  const parts = ['pv-shape', `pv-shape--${geometry.type}`, `pv-status-${measurement.status}`];
  if (geometry.type === 'rect' && role !== 'frame') parts.push('pv-shape--zone');
  if (measurement.severity === 'info' && role === 'guide') parts.push('pv-shape--soft');
  if (highlighted) parts.push('pv-shape--highlight');
  if (role === 'frame') parts.push('pv-shape--frame');
  return parts.join(' ');
};

export const buildOverlayPlan = (
  report: ValidationReport | undefined,
  options: OverlayPlanOptions,
): OverlayPlan => {
  const plan: OverlayPlan = {
    shapes: [],
    labels: [],
    problemMarkers: [],
    landmarks: [],
    drawn: 0,
    hidden: 0,
  };
  if (!report) return plan;

  const seenLabels = new Set<string>();
  let index = 0;

  for (const measurement of report.measurements) {
    if (!shouldDraw(measurement, options)) {
      plan.hidden += 1;
      continue;
    }
    plan.drawn += 1;
    const highlighted = measurement.id === options.highlightId;
    const role: OverlayShape['role'] = measurement.guide
      ? 'guide'
      : measurement.id === 'aspect_ratio'
        ? 'frame'
        : measurement.severity === 'error' && measurement.status === 'fail'
          ? 'problem'
          : 'measurement';

    for (const geometry of measurement.geometry) {
      const attributes = geometryToAttributes(geometry, options.baseSize);
      if (!attributes) continue;
      const isPolygon = geometry.type === 'polygon';
      plan.shapes.push({
        key: `${measurement.id}:${index}`,
        measurementId: measurement.id,
        status: measurement.status,
        severity: measurement.severity,
        role,
        element: attributeNameFor(geometry),
        attributes,
        ...(isPolygon ? { polygonPoints: polygonPoints(geometry) } : {}),
        className: shapeClassName(role, measurement, highlighted, geometry),
        title: measurement.message,
      });
      index += 1;
    }

    /**
     * Normal mode keeps exactly three captions: CENTER, EYES and HEAD ZONE.
     * Everything else — measured values on the photo, TOP/CHIN boundaries, the
     * head-height bracket caption — belongs to the detailed overlay. The numbers
     * themselves are in the checklist, where they can be compared with the
     * official range.
     */
    const CALM_LABELS = new Set(['guide.center_line', 'guide.eye_band', 'guide.head_zone']);
    const label =
      options.detailMode || CALM_LABELS.has(measurement.id)
        ? labelFor(measurement, report, options)
        : null;
    if (label && !seenLabels.has(label.id)) {
      seenLabels.add(label.id);
      plan.labels.push(label);
    }
    for (const boundary of options.detailMode ? boundaryLabelsFor(measurement, options) : []) {
      if (seenLabels.has(boundary.id)) continue;
      seenLabels.add(boundary.id);
      plan.labels.push(boundary);
    }
  }

  if (options.detailMode) {
    for (const [landmarkIndex, landmark] of (report.metrics.geometry?.landmarks ?? []).entries()) {
      // Every third point is plenty for a debug view and keeps the DOM light.
      if (landmarkIndex % 3 !== 0) continue;
      plan.landmarks.push({ x: r(landmark.x), y: r(landmark.y) });
    }
  }

  // Problem pins are part of the detailed mode; the normal view stays minimal.
  if (!options.detailMode) return plan;

  plan.problemMarkers = report.problems.map((problem) => ({
    id: problem.id,
    checkId: problem.checkId,
    rect: problem.rect,
    message: problem.message,
    severity: problem.severity,
  }));

  return plan;
};

/** Colors are presentation only; they come from the status, never from a score. */
export const statusColorVar = (status: MeasurementStatus): string => `var(--pv-${status === 'not_checked' ? 'neutral' : status})`;
